import type React from 'react';
import { useCallback } from 'react';

import {
	useSignerVelocityExperiment,
	useTrackSignerVelocitySuppressed,
	useTreatSignerVelocityExperiment,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { Waypoint } from '#src/app/shared/components/scroll';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';

/**
 * The petition whose pill impression was recorded most recently.
 *
 * Module-scoped, so the two layout columns — both mounted, one hidden by CSS — share one guard across a
 * breakpoint-crossing resize. Only the last petition, not a Set of every one seen: `useCallOnce` keeps a
 * single last-deps array, so an A → B → A navigation fires `$exposure` three times, and a Set would cap
 * impressions at two.
 */
let lastImpressionPetitionId: string | null = null;

/** @internal Test-only: module state persists between test cases otherwise. */
export function resetSignerVelocityImpressionsForTests(): void {
	lastImpressionPetitionId = null;
}

/**
 * Arm-independent tracking for the signer velocity experiment: treats the experiment and fires
 * `$exposure` on qualification, and `petition_page_signer_velocity_suppressed` when an in-scope visitor
 * can't be shown a pill.
 *
 * Renders nothing. Must be mounted exactly once, from a location that renders in every arm — mounting it
 * inside a pill would mean control and fake_control never fire.
 */
export const SignerVelocityTracking: React.FC = () => {
	useTreatSignerVelocityExperiment();
	useTrackSignerVelocitySuppressed();

	return null;
};

/**
 * Fires `petition_page_signer_velocity_pill_impression` once per petition, when the pill actually reaches
 * the viewport. Render this alongside the pill, so it only mounts in the treatment arm.
 *
 * Viewport-gated, unlike `$exposure` — it measures whether the pill was seen, and must not be used as an
 * analysis denominator.
 *
 * `onEnter`, not `onPositionChange`: the latter fires on every delivered entry, and a `display:none` copy
 * of the pill has an all-zero rect that `calculatePosition` classifies as `'inside'`.
 */
export const SignerVelocityPillImpression: React.FC = () => {
	const { placement, dailySignatureCount, floor, isRealAssignment, petitionId } = useSignerVelocityExperiment();
	const trackOnce = useTrackOnce([placement, petitionId]);

	const onEnter = useCallback(() => {
		if (placement === 'none') return;
		// Fallback and QA-override sessions are never exposed, so their impressions would inflate the seen-rate.
		if (!isRealAssignment) return;
		if (lastImpressionPetitionId === petitionId) return;
		lastImpressionPetitionId = petitionId;
		void trackOnce('petition_page_signer_velocity_pill_impression', {
			petition_id: petitionId,
			signer_velocity_placement: placement,
			daily_signature_count: dailySignatureCount,
			signer_velocity_floor: floor,
		});
	}, [placement, isRealAssignment, petitionId, dailySignatureCount, floor, trackOnce]);

	return <Waypoint onEnter={onEnter} />;
};
